import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { SessionResponse } from './types';

import { codeSessionKeys } from './queries';
import { codeRuntimeOf } from './runtime/runtime-for';
import { listsFor } from './runtime/session-lists';

export function codeActionExcerpt(text: string): string {
  const singleLine = text.replace(/\s+/g, ' ').trim();
  return singleLine.length > 120 ? `${singleLine.slice(0, 119)}…` : singleLine;
}

export function codeSessionActionDescription(
  client: QueryClient,
  sessionId: string,
  prompt?: string,
): string {
  const session =
    client.getQueryData<SessionResponse>(codeSessionKeys.detail(sessionId))?.session ??
    listsFor(sessionId).find(client, sessionId);
  return [placeOf(session), ...(prompt ? [codeActionExcerpt(prompt)] : [])].join('\n');
}

/** Where an action's toast says it happened: the session's title and its repository or folder. */
function placeOf(session: { id: string; title: string; repoFullName: string } | undefined): string {
  if (session === undefined) {
    return 'Code session';
  }
  const title = codeRuntimeOf(session.id).words.ownTitle(session);
  if (title === null) {
    return session.repoFullName;
  }
  return `${codeActionExcerpt(title)} · ${session.repoFullName}`;
}

/**
 * Keep progress and its result in one toast, even if the initiating view unmounts.
 * Omitting success dismisses the loading toast when the operation completes. Omitting both
 * shows nothing unless the operation fails.
 */
export function withCodeSessionToast<T>(
  operation: () => Promise<T>,
  messages: {
    loading?: string;
    success?: string;
    error: string;
    description: string;
    /**
     * What the error toast adds for the failure it reports, such as the Retry that starts a
     * local server again, and how long it stays for that button to be pressed.
     */
    onError?: (error: unknown) => {
      action?: { label: string; onClick: () => void };
      duration?: number;
    };
  },
): Promise<T> {
  const promise = Promise.resolve().then(operation);
  toast.promise(promise, {
    loading: messages.loading,
    success: messages.success,
    description: messages.description,
    descriptionClassName: 'whitespace-pre-line',
    error: (error: unknown) => ({
      message: messages.error,
      description: [messages.description, error instanceof Error ? error.message : undefined]
        .filter(Boolean)
        .join('\n'),
      // With no loading toast before it, the failure is a toast of its own and takes no class
      // from one, so it names its own to keep the lines apart.
      descriptionClassName: 'whitespace-pre-line',
      ...messages.onError?.(error),
    }),
  });
  return promise;
}
